import { useEffect, useRef, useState } from "react";
import { LuFilter } from "react-icons/lu";
import { MdOutlineDelete } from "react-icons/md";
import { useSearchParams } from "react-router-dom";
import Button from "../../../components/Button/Button";
import FilterApplied from "../../../components/FilterApplied/FilterApplied";
import Input from "../../../components/Input/Input";
import ModalComponent from "../../../components/Modal/ModalComponent";
import SearchInput from "../../../components/SearchInput/SearchInput";
import Select from "../../../components/Select/Select";
import { UnitSelector } from "../../../components/UnitSelector/UnitSelector";
import { FiDownload } from "react-icons/fi";
import LicenseService from "../../../services/LicensesService";
import { useTranslation } from "react-i18next";
import download from "downloadjs";
import { NotificationManager } from "react-notifications";

const EMPTY_FILTERS = {
   search: "",
   tag: "",
   serialNumber: "",
   technicalStatus: "",
   allocationStatus: "",
   ownerUnitId: "",
   allocatedUnitId: "",
   typeCode: "",
   billingStatus: "",
   dueDateFrom: "",
   dueDateTo: "",
};

const FILTER_KEYS = Object.keys(EMPTY_FILTERS);

function readFiltersFromUrl(searchParams) {
   return FILTER_KEYS.reduce((filters, key) => {
      filters[key] = searchParams.get(key) ?? "";
      return filters;
   }, {});
}

const FILTER_LABELS = {
   search: "Busca",
   tag: "Licença / Tag",
   serialNumber: "Dispositivo",
   technicalStatus: "Status",
   allocationStatus: "Status de alocação",
   ownerUnitId: "Proprietário",
   allocatedUnitId: "Unidade alocada",
   typeCode: "Tipo",
   billingStatus: "Status financeiro",
   dueDateFrom: "Vencimento a partir de",
   dueDateTo: "Vencimento até",
};

const FILTER_VALUE_LABELS = {
   technicalStatus: {
      ACTIVE: "Ativo",
      INACTIVE: "Inativo",
      BLOCKED: "Bloqueado",
      TERMINATED: "Encerrado",
   },
   allocationStatus: {
      FREE: "Livre",
      ACTIVE: "Alocada",
      TRANSFERED: "Transferida",
   },
   billingStatus: {
      PENDING: "Pendente",
      PAID: "Pago",
      CANCELED: "Cancelado",
   },
};

const TECHNICAL_STATUS_OPTIONS = [
   { value: "ACTIVE", text: "Ativo" },
   { value: "INACTIVE", text: "Inativo" },
   { value: "BLOCKED", text: "Bloqueado" },
   { value: "TERMINATED", text: "Encerrado" },
];

const ALLOCATION_STATUS_OPTIONS = [
   { value: "FREE", text: "Livre" },
   { value: "ACTIVE", text: "Alocada" },
   { value: "TRANSFERED", text: "Transferida" },
];

const BILLING_STATUS_OPTIONS = [
   { value: "PENDING", text: "Pendente" },
   { value: "PAID", text: "Pago" },
   { value: "CANCELED", text: "Cancelado" },
];

const LicenseFilters = ({ filtersApplied }) => {
   const { t } = useTranslation();
   const [searchParams, setSearchParams] = useSearchParams();
   const [filters, setFilters] = useState(() => readFiltersFromUrl(searchParams));
   const [draftFilters, setDraftFilters] = useState(() => readFiltersFromUrl(searchParams));
   const [searchInput, setSearchInput] = useState(() => searchParams.get("search") ?? "");
   const filtersRef = useRef(filters);
   const searchDebounceRef = useRef(null);
   const initializedFromUrlRef = useRef(false);
   const [modalIsOpen, setModalIsOpen] = useState(false);
   const serializedSearchParams = searchParams.toString();

   const [isExporting, setIsExporting] = useState(false);
   const [exportedBytes, setExportedBytes] = useState(0);

   useEffect(() => {
      const restoredFilters = readFiltersFromUrl(new URLSearchParams(serializedSearchParams));
      const isInitialRestore = !initializedFromUrlRef.current;
      initializedFromUrlRef.current = true;
      const filtersUnchanged = FILTER_KEYS.every(
         (key) => filtersRef.current[key] === restoredFilters[key],
      );

      setSearchInput(restoredFilters.search);
      if (filtersUnchanged && !isInitialRestore) {
         setDraftFilters(restoredFilters);
         return;
      }

      filtersRef.current = restoredFilters;
      setFilters(restoredFilters);
      setDraftFilters(restoredFilters);
      filtersApplied(restoredFilters);
   }, [serializedSearchParams, filtersApplied]);

   const syncFiltersToUrl = (nextFilters) => {
      const nextSearchParams = new URLSearchParams(searchParams);
      FILTER_KEYS.forEach((key) => {
         if (nextFilters[key]) {
            nextSearchParams.set(key, nextFilters[key]);
         } else {
            nextSearchParams.delete(key);
         }
      });
      setSearchParams(nextSearchParams, { replace: true });
   };

   const applyFilters = (nextFilters) => {
      filtersRef.current = nextFilters;
      setFilters(nextFilters);
      filtersApplied(nextFilters);
      syncFiltersToUrl(nextFilters);
   };

   useEffect(() => () => {
      clearTimeout(searchDebounceRef.current);
   }, []);

   const updateDraft = (key, value) => {
      setDraftFilters((current) => ({ ...current, [key]: value }));
   };

   const applyDraft = () => {
      clearTimeout(searchDebounceRef.current);
      const appliedFilters = { ...draftFilters, search: searchInput };
      setDraftFilters(appliedFilters);
      applyFilters(appliedFilters);
      setModalIsOpen(false);
   };

   const clearFilters = () => {
      clearTimeout(searchDebounceRef.current);
      const emptyFilters = { ...EMPTY_FILTERS };
      setSearchInput("");
      setDraftFilters(emptyFilters);
      applyFilters(emptyFilters);
      setModalIsOpen(false);
   };

   const removeFilter = (key) => {
      if (key === "search") {
         clearTimeout(searchDebounceRef.current);
         setSearchInput("");
      }
      const updatedFilters = { ...filtersRef.current, [key]: "" };
      setDraftFilters(updatedFilters);
      applyFilters(updatedFilters);
   };

   const changeSearch = (value) => {
      setSearchInput(value);
      setDraftFilters((current) => ({ ...current, search: value }));
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = setTimeout(() => {
         const updatedFilters = { ...filtersRef.current, search: value };
         applyFilters(updatedFilters);
      }, 500);
   };

   const activeFilters = Object.entries(filters).filter(([, value]) => value);

   const downloadCsv = async () => {
      let fileHandle;
      if (window.showSaveFilePicker) {
         try {
            fileHandle = await window.showSaveFilePicker({
               suggestedName: "licenses.csv",
               types: [{
                  description: "CSV",
                  accept: { "text/csv": [".csv"] },
               }],
            });
         } catch (error) {
            if (error.name === "AbortError") {
               return;
            }
            NotificationManager.error("", "Não foi possível iniciar o download do CSV", 5000);
            return;
         }
      }

      setIsExporting(true);
      setExportedBytes(0);
      let fileWriter;
      const chunks = [];

      try {
         const response = await LicenseService.exportLicensesToCsv(filters);
         if (!response?.body) {
            return;
         }

         fileWriter = fileHandle ? await fileHandle.createWritable() : null;
         const reader = response.body.getReader();

         while (true) {
            const { done, value } = await reader.read();
            if (done) {
               break;
            }

            setExportedBytes((bytes) => bytes + value.byteLength);
            if (fileWriter) {
               await fileWriter.write(value);
            } else {
               chunks.push(value);
            }
         }

         if (fileWriter) {
            await fileWriter.close();
         } else {
            download(
               new Blob(chunks, { type: "text/csv;charset=utf-8;" }),
               "licenses.csv",
               "text/csv;charset=utf-8;",
            );
         }
      } catch (error) {
         if (fileWriter) {
            try {
               await fileWriter.abort();
            } catch (abortError) {
               console.error("Erro ao cancelar a gravação do CSV", abortError);
            }
         }
         console.error("Erro ao exportar licenças para CSV", error);
         NotificationManager.error("", "Erro ao exportar licenças para CSV", 5000);
      } finally {
         setIsExporting(false);
      }
   };

   return (
      <>
         <div className="flex flex-wrap items-center justify-between gap-2.5">
            <div className="min-w-60">
               <SearchInput
                  value={searchInput}
                  placeholder="Buscar"
                  onChange={({ target }) => changeSearch(target.value)}
               />
            </div>
            <div className="flex flex-wrap items-center gap-2.5">
               <Button
                  className="button-white events-filters-button"
                  label="Filtros"
                  icon={<LuFilter size={18} />}
                  onClick={() => {
                     setDraftFilters(filters);
                     setModalIsOpen(true);
                  }}
               />
               <Button
                  className="button-white events-filters-button"
                  label="Limpar filtros"
                  icon={<MdOutlineDelete size={17} />}
                  onClick={clearFilters}
               />
               <Button
                  className="button-white events-filters-button"
                  label={isExporting ? `Recebendo CSV (${(exportedBytes / 1024 / 1024).toFixed(1)} MB)` : t("common.exportCSV")}
                  icon={<FiDownload size={17} />}
                  onClick={downloadCsv}
                  disabled={isExporting}
               />
               <Button
                  className="button-blue adicionar-button"
                  action="add"
                  label={`Adicionar licença`}
                  navigate={"../licenses/new"}
               />
            </div>
         </div>

         {!!activeFilters.length && (
            <div className="flex flex-wrap gap-2 mt-3!">
               {activeFilters.map(([key, value]) => (
                  <FilterApplied
                     key={key}
                     filter={`${FILTER_LABELS[key]}: ${FILTER_VALUE_LABELS[key]?.[value] ?? value}`}
                     removeFilter={() => removeFilter(key)}
                  />
               ))}
            </div>
         )}

         <ModalComponent
            modalIsOpen={modalIsOpen}
            modalText="Filtros avançados de licenças"
            handleConfirm={applyDraft}
            handleCancel={() => setModalIsOpen(false)}
         >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 min-w-[min(80vw,700px)]">
               <Input
                  labelText="Licença / Tag"
                  value={draftFilters.tag}
                  onChange={({ target }) => updateDraft("tag", target.value)}
               />
               <Input
                  labelText="Dispositivo / Serial Number"
                  value={draftFilters.serialNumber}
                  onChange={({ target }) => updateDraft("serialNumber", target.value)}
               />
               <Select
                  labelText="Status técnico"
                  value={draftFilters.technicalStatus}
                  options={TECHNICAL_STATUS_OPTIONS}
                  onChange={({ target }) => updateDraft("technicalStatus", target.value)}
               />
               <Select
                  labelText="Status de alocação"
                  value={draftFilters.allocationStatus}
                  options={ALLOCATION_STATUS_OPTIONS}
                  onChange={({ target }) => updateDraft("allocationStatus", target.value)}
               />
               <UnitSelector
                  inputLabel="Proprietário"
                  modalTitle="Selecione o proprietário"
                  parentId={draftFilters.ownerUnitId || null}
                  setParentId={(unitId) => updateDraft("ownerUnitId", unitId ? String(unitId) : "")}
               />
               <UnitSelector
                  inputLabel="Unidade alocada"
                  modalTitle="Selecione a unidade alocada"
                  parentId={draftFilters.allocatedUnitId || null}
                  setParentId={(unitId) => updateDraft("allocatedUnitId", unitId ? String(unitId) : "")}
               />
               <Input
                  labelText="Tipo da licença"
                  value={draftFilters.typeCode}
                  onChange={({ target }) => updateDraft("typeCode", target.value)}
               />
               <Select
                  labelText="Status financeiro"
                  value={draftFilters.billingStatus}
                  options={BILLING_STATUS_OPTIONS}
                  onChange={({ target }) => updateDraft("billingStatus", target.value)}
               />
               <Input
                  labelText="Vencimento a partir de"
                  type="date"
                  value={draftFilters.dueDateFrom}
                  onChange={({ target }) => updateDraft("dueDateFrom", target.value)}
               />
               <Input
                  labelText="Vencimento até"
                  type="date"
                  value={draftFilters.dueDateTo}
                  onChange={({ target }) => updateDraft("dueDateTo", target.value)}
               />
            </div>
         </ModalComponent>
      </>
   );
};

export default LicenseFilters;
